import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  SubmitTarget,
  useLoaderData,
} from "react-router";
import {
  NewUserRequest,
  UserOrganizationAccess,
  UserRole,
} from "../infrastructure/api/api";
import { NyvaError } from "../infrastructure/errors/errors";
import { MissingLinkError } from "../infrastructure/entrypoint/link";
import { organizationContext } from "../infrastructure/organization/OrganizationContext";
import { usersApi } from "./usersApi";

export interface UsersRouteData {
  users: UserOrganizationAccess[];
  canManageUsers: boolean;
}

export const usersLoader = async ({
  context,
}: LoaderFunctionArgs): Promise<UsersRouteData> => {
  const api = usersApi(context.get(organizationContext));

  console.log("api is: " + JSON.stringify(api));

  return {
    users: await api.listUsers(),
    canManageUsers: api.canManageUsers(),
  };
};

export const useUsersRouteData = (): UsersRouteData =>
  useLoaderData<UsersRouteData>();

type UsersActionIntent = "add" | "delete";

// UserResource::addUser / UserResource::deleteUser throw UserManagementException
// with these exact messages. There is no dedicated error code, so we key off the
// message text to show a more specific error to the user.
export type UserActionErrorReason =
  | "invalid_nin"
  | "role_required"
  | "already_has_access"
  | "cannot_delete_self"
  | "user_not_found"
  | "missing_link"
  | "unknown";

const ERROR_REASON_BY_MESSAGE: Record<string, UserActionErrorReason> = {
  "Invalid NIN": "invalid_nin",
  "Role is required": "role_required",
  "User already has access to this organization": "already_has_access",
  "Cannot delete yourself": "cannot_delete_self",
  "No user found": "user_not_found",
};

const errorReason = (err: unknown): UserActionErrorReason => {
  if (err instanceof MissingLinkError) {
    return "missing_link";
  }

  if (err instanceof NyvaError && err.apiErrorMessage) {
    return ERROR_REASON_BY_MESSAGE[err.apiErrorMessage] ?? "unknown";
  }

  return "unknown";
};

export type UsersActionResult =
  | { ok: true; intent: UsersActionIntent }
  | {
      error: string;
      reason: UserActionErrorReason;
      missingLink: boolean;
      intent: UsersActionIntent;
    };

interface AddUserPayload extends NewUserRequest {
  intent: "add";
}

interface DeleteUserPayload {
  intent: "delete";
  nin: string;
}

type UsersActionPayload = AddUserPayload | DeleteUserPayload;

const failure = (
  intent: UsersActionIntent,
  err: unknown,
): UsersActionResult => {
  console.error(`Failed to ${intent} user:`, err);

  return {
    intent,
    missingLink: err instanceof MissingLinkError,
    reason: errorReason(err),
    error: err instanceof Error ? err.message : "Unknown error",
  };
};

export const usersAction = async ({
  request,
  context,
}: ActionFunctionArgs): Promise<UsersActionResult> => {
  const payload = (await request.json()) as UsersActionPayload;
  const api = usersApi(context.get(organizationContext));

  if (payload.intent === "delete") {
    try {
      await api.deleteUser(payload.nin);
      return { ok: true, intent: "delete" };
    } catch (err) {
      return failure("delete", err);
    }
  }

  try {
    await api.addUser({ nin: payload.nin, role: payload.role });
    return { ok: true, intent: "add" };
  } catch (err) {
    return failure("add", err);
  }
};

export const buildAddUserPayload = (
  nin: string,
  role: UserRole,
): SubmitTarget => ({ intent: "add", nin, role });

export const buildDeleteUserPayload = (nin: string): SubmitTarget => ({
  intent: "delete",
  nin,
});
